import { zFightingFixDistance } from "@dextall/panels-defaults";
import { isMoreThan } from "@dextall/shared";
import { BoundaryLoopsFactory } from "../boundaries/boundaryLoopsFactory";
import customComponentColorsFactory from "../colors/customComponentColorsFactory";
import { claddingCellGap } from "../defaults";
import {
    CladdingCellExtrusionGeometryFactory,
    ExtrusionGeometry,
    ExtrusionGeometryRegistration,
} from "../geometry/claddingCellExtrusionGeometryFactory";
import { createLouvreGrillPlaneGeometry } from "./panelLouvreGrillGeometry";
import type { WindowOverlapsSetting } from "../../../responses/systemSettings";
import type { BoundaryLoop } from "../boundaries/boundaryLoop";

const solidThickness = 0.01;
const grillGeometryGroupName = "louvre-grills";

export class PanelLouvreGrill {
    private readonly geometryLoop: BoundaryLoop;
    private readonly grillMaterial: THREE.Material;
    private readonly solidMaterial: THREE.Material;

    constructor(
        openingBoundaryLoop: BoundaryLoop,
        louvreFrameOffset: WindowOverlapsSetting,
        color: number,
        boundaryLoopsFactory: BoundaryLoopsFactory,
        private readonly claddingExtrusionFactory: CladdingCellExtrusionGeometryFactory,
    ) {
        this.grillMaterial = customComponentColorsFactory.createLouvreGrillMaterial(`RAL ${color}`);
        this.solidMaterial = customComponentColorsFactory.createCladdingCellMaterial(`RAL ${color}`);
        this.geometryLoop = this.computeGeometryLoop(openingBoundaryLoop, louvreFrameOffset, boundaryLoopsFactory);
    }

    createGeometry(): ExtrusionGeometry[] {
        const box = this.geometryLoop.box;
        const size = box.getSize();

        const planeGeometry = createLouvreGrillPlaneGeometry(box);

        const grillRegistration: ExtrusionGeometryRegistration = {
            type: "named-shape",
            name: grillGeometryGroupName,
            size: new THREE.Vector3(size.x, size.y, 0),
        };

        const frontGrill: ExtrusionGeometry = {
            ...grillRegistration,
            geometry: planeGeometry,
            matrix: this.claddingExtrusionFactory.computeGeometryTransform(this.geometryLoop, {
                thickness: 0,
                zOffset: zFightingFixDistance,
            }),
            material: this.grillMaterial,
        };

        const solidBody = this.claddingExtrusionFactory.createCladdingCellExtrusion(this.geometryLoop, {
            material: this.solidMaterial,
            thickness: solidThickness,
            zOffset: -solidThickness,
        });

        const backGrill: ExtrusionGeometry = {
            ...grillRegistration,
            geometry: planeGeometry,
            matrix: this.claddingExtrusionFactory.computeGeometryTransform(this.geometryLoop, {
                thickness: 0,
                zOffset: -solidThickness - zFightingFixDistance,
            }),
            material: this.grillMaterial,
        };

        return [frontGrill, solidBody, backGrill];
    }

    private computeGeometryLoop(
        openingBoundaryLoop: BoundaryLoop,
        louvreFrameOffset: WindowOverlapsSetting,
        boundaryLoopsFactory: BoundaryLoopsFactory,
    ): BoundaryLoop {
        const size = openingBoundaryLoop.box.getSize();

        const minRequiredWidth = louvreFrameOffset.left + louvreFrameOffset.right + claddingCellGap;
        const minRequiredHeight = louvreFrameOffset.top + louvreFrameOffset.bottom + claddingCellGap;

        if (!isMoreThan(size.x, minRequiredWidth) || !isMoreThan(size.y, minRequiredHeight)) {
            return openingBoundaryLoop;
        }

        const box = new THREE.Box3(
            new THREE.Vector3()
                .copy(openingBoundaryLoop.box.min)
                .add(new THREE.Vector3(louvreFrameOffset.left, louvreFrameOffset.bottom, 0)),
            new THREE.Vector3()
                .copy(openingBoundaryLoop.box.max)
                .add(new THREE.Vector3(-louvreFrameOffset.right, -louvreFrameOffset.top, 0)),
        );

        return boundaryLoopsFactory.createLoopFromBox(box, openingBoundaryLoop.transform);
    }
}
